/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    string, shape, object, func, bool,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    Button, CircularProgress,
} from '@material-ui/core';
import Caret from '@material-ui/icons/ArrowForwardIos';

const styles = (theme) => ({
    container: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 18px',
        marginBottom: '40px',
        border: '1px solid rgba(0, 0, 0, 0.3)',
        [theme.breakpoints.down(601)]: {
            padding: '18px',
        },
        [theme.breakpoints.down(900)]: {
            justifyContent: 'center',
        },
    },
    buttonLink: {
        fontSize: '0.875rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        gap: '7px',
        margin: '0px',
        '& svg': {
            width: '0.5em',
        },
    },
    personalizationMessage: {
        fontSize: '0.875rem',
        lineHeight: '1.75rem',
        margin: '0px',
        textAlign: 'left',
    },
    personalizationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        [theme.breakpoints.down(768)]: {
            flexDirection: 'column',
        },
    },
    personalizationAdded: {
        margin: '0px',
        [theme.breakpoints.down(768.5)]: {
            paddingBottom: '7px',
        },
    },
    personalizationAddedContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        textAlign: 'left',

        [theme.breakpoints.down(768.5)]: {
            flexDirection: 'column',
            alignItems: 'self-start',
            fontSize: '0.875rem',
            lineHeight: '1.125rem',
        },
    },
    editButton: {
        border: `1px solid ${theme.palette.cms?.black || '#000000'}`,
        background: '#FDF9EF',
        borderRadius: 'inherit',
        fontSize: '0.875rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
    },
    removeButton: {
        textDecoration: 'underline',
        fontSize: '0.875rem',
        lineHeight: '1.75rem',
        fontWeight: 400,
        textTransform: 'capitalize',
        '&:hover': {
            backgroundColor: theme.palette.cms?.white || '#ffffff',
        },
    },
    buttonContainer: {
        textAlign: 'center',
        display: 'flex',
        width: '100%',
        gap: '15px',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.down(768.5)]: {
            gap: '30px',
        },
        [theme.breakpoints.down(900)]: {
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-evenly',
        },
    },
    imageContainer: {
        position: 'relative',
        [theme.breakpoints.down(900)]: {
            '& $giftImage': {
                display: 'none',
            },
        },
    },
    giftImage: {
        display: 'block',
    },
    imageWidth: {
        [theme.breakpoints.down(900)]: {
            width: '25%',
        },
    },
    coverImage: {
        width: '68px',
        height: '45px',
        position: 'absolute',
        top: '5px',
        left: '5%',
    },
    buttonWrapper: {
        display: 'flex',
    },
    button: {
        margin: '0 10px',
        color: theme.palette.common?.black,
        fontFamily: theme.typography.fontFamily,
        height: '30px',
        width: '75px',
        [theme.breakpoints.down(768.5)]: {
            margin: '0px 14px 0px 0px',
        },
    },
    typography: {
        fontWeight: 'bold',
        marginBottom: '5px',
        [theme.breakpoints.down(900)]: {
            margin: '0px',
        },
    },
    labelType: {
        fontSize: '0.875rem',
        margin: '0px',
        textAlign: 'center',
    },
    message: {
        fontSize: '0.875rem',
        margin: '0px',
        marginTop: '2px',
        [theme.breakpoints.down(480)]: {
            whiteSpace: 'nowrap',
            fontSize: '0.813',
        },
    },
    customWrapper: {
        minWidth: '80px',
        maxWidth: '60px',
        maxHeight: '57px',
        minHeight: '57px',
        borderRadius: '2px',
        backgroundSize: 'cover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    personalizeMessage: {
        width: '120px',
        margin: '5px 0',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        [theme.breakpoints.down(900)]: {
            width: '75px',
            margin: '5px 0px 0px 0px',
        },
    },
    CircularProgress: {
        textAlign: 'center',
        margin: '0 auto',
    },
    loaderOverlay: {
        display: 'flex',
        zIndex: 9,
        position: 'absolute',
        background: 'rgba(255,255,255,.7)',
        top: '3px',
        textAlign: 'center',
        left: '3px',
        bottom: '3px',
        right: '3px',
        verticalAlign: 'middle',
        alignItems: 'center',
    },
    mainContainer: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        width: '100%',
    },
    wrapper: {
        [theme.breakpoints.down(600)]: {
            width: 'calc(100% - 32px)',
            margin: '0 auto 24px',
        },
    },
});

const GCIPersonalizationV2 = ({
    classes,
    gciKey,
    gciData,
    loader,
    loading,
    featureFlags,
    placementText,
    cgcMessageData,
    editCGCLabelData,
    setModalState,
    removeGCIData,
    isPdpRedesignEnabled,
}) => (
    <div className={`${classes.container} ${isPdpRedesignEnabled ? classes.wrapper : ''}`}>
        <div className={classes.buttonContainer}>
            {loading
                ? (
                    <p className={classes.typography}>Loading...</p>
                ) : (
                    <>
                        {featureFlags['is-cgc-api-from-main-url'] || gciKey
                            ?  (
                                <>
                                    { gciData?.key_id
                                        ?  (
                                            <>
                                                <div className={`${classes.imageContainer} ${gciData?.key_id ? classes.imageWidth : ''}`}>
                                                    {gciData?.key_id
                                                                            && (
                                                                                <>
                                                                                    <div className={classes.customWrapper} style={{ backgroundImage: `url(${featureFlags['is-cgc-api-from-main-url'] ? gciData.coverImage : gciData.thumbnail})` }}>
                                                                                        <p className={classes.labelType}>
                                                                                            {
                                                                                                gciData.text
                                                                                                    && (
                                                                                                        (featureFlags['is-cgc-api-from-main-url'] && gciData.thumbnail)
                                                                                                        || (!featureFlags['is-cgc-api-from-main-url'] && gciData.coverImage)
                                                                                                    )
                                                                                                    ? 'Photo And Message'
                                                                                                    : `${gciData.text ? 'Your Message' : placementText}`
                                                                                            }
                                                                                        </p>
                                                                                    </div>
                                                                                    {
                                                                                        featureFlags['is-cgc-api-from-main-url']
                                                                                            ? (
                                                                                                <>
                                                                                                    {gciData?.thumbnail && <img className={classes.coverImage} src={gciData?.thumbnail} alt="coverImage" />}
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    {gciData?.coverImage && <img className={classes.coverImage} src={gciData?.coverImage} alt="coverImage" />}
                                                                                                </>
                                                                                            )
                                                                                    }
                                                                                    {gciData.text ? <p className={classes.personalizeMessage}>{gciData.text}</p> : ''}
                                                                                </>
                                                                            )}
                                                </div>
                                                <div className={classes.personalizationAddedContainer}>
                                                    <p className={classes.personalizationAdded}>Personalization added for {cgcMessageData?.price_message || '$5.00'}!</p>
                                                    <div className={classes.buttonWrapper}>
                                                        <Button data-testid="gci-button-edit" className={`${classes.button} ${classes.editButton}`} color="primary" onClick={() => { editCGCLabelData(); }}>Edit</Button>
                                                        <Button data-testid="gci-button-remove" className={`${classes.button} ${classes.removeButton}`} color="primary" onClick={() => { removeGCIData(); }}>Remove</Button>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                        : (
                                            <div className={classes.mainContainer} role="presentation" onKeyDown={() => { setModalState(true); }} onClick={() => { setModalState(true); }}>
                                                <img src="https://images.contentstack.io/v3/assets/blt89dbf1c763ec00a6/blt549006ae89480e39/633709c175c0be225b8d9f9a/personalization_image.svg?auto=webp" alt="Personalization" />
                                                <div className={classes.personalizationContainer}>
                                                    <p className={classes.personalizationMessage}>Add a photo, personal message, or both!</p>
                                                    <p className={classes.buttonLink}>Personalize it FOR ONLY {cgcMessageData?.price_message || '$5.00'}! <Caret /></p>
                                                </div>
                                            </div>
                                        )}
                                </>
                            )
                            :                            (
                                <p className={classes.typography}>Apologies, but we are unable to add personalization at this time.</p>
                            )}
                    </>
                )}
        </div>
    </div>
);

GCIPersonalizationV2.propTypes = {
    classes: object.isRequired,
    gciKey: string,
    gciData: shape({
        key_id: string,
        text: string,
        thumbnail: string,
    }),
    loader: bool,
    featureFlags: object.isRequired,
    setModalState: func,
    editCGCLabelData: func,
    removeGCIData: func,
    loading: bool,
    placementText: string,
    cgcMessageData: string,
    isPdpRedesignEnabled: bool,
};

GCIPersonalizationV2.defaultProps = {
    gciKey: '',
    gciData: {
        key_id: '',
        text: '',
        thumbnail: '',
    },
    loader: false,
    setModalState: () => {},
    editCGCLabelData: () => {},
    removeGCIData: () => {},
    loading: false,
    placementText: '',
    cgcMessageData: '',
    isPdpRedesignEnabled: false,
};

export default withStyles(styles)(GCIPersonalizationV2);
